import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import i18n from '@/i18n';
import { api } from '@/api/client';

export type Language = 'es' | 'en';

export const SUPPORTED_LANGUAGES: Language[] = ['es', 'en'];

interface LanguageContextType {
  language: Language;
  setLanguage: (language: Language) => void;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

// Same key used by i18n init to resolve the startup language.
export const LANGUAGE_STORAGE_KEY = 'activeid-language';
const TOKEN_KEY = 'activeid_token';

function normalize(lang: string | undefined): Language {
  return lang?.toLowerCase().startsWith('en') ? 'en' : 'es';
}

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [language, setLanguageState] = useState<Language>(() => normalize(i18n.language));

  // Keep local state in sync with any caller that changes the language
  // directly through i18n (e.g. AuthContext applying the server preference).
  useEffect(() => {
    const onChange = (lng: string) => setLanguageState(normalize(lng));
    i18n.on('languageChanged', onChange);
    return () => i18n.off('languageChanged', onChange);
  }, []);

  const setLanguage = (next: Language) => {
    localStorage.setItem(LANGUAGE_STORAGE_KEY, next);
    i18n.changeLanguage(next); // triggers languageChanged -> updates state

    // Persist to the user's backend profile so the choice follows them across
    // devices. Fire-and-forget; only attempted when authenticated.
    if (localStorage.getItem(TOKEN_KEY)) {
      api.post('/api/auth/me/language', { language: next }).catch(() => {
        // Non-fatal: the local choice still applies for this session.
      });
    }
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}
